/**
 * EPIC-031 STORY-1102 phase 2 follow-on — builder-code referral capture.
 *
 * Polymarket's builder model: a trader who arrives via a builder's
 * link or embed carries that builder's 32-byte attribution code on
 * every order they place. The backend (`OrdersService.place`) accepts
 * it on the order body as `builderCodeHex`; this module is the
 * frontend half that captures + persists the code so it rides on
 * orders placed in subsequent sessions.
 *
 * Capture path:
 *   1. User lands on any page with `?bc=<64-hex>` in the URL.
 *   2. The first read on the client validates the shape (exactly
 *      64 lowercase or uppercase hex chars), normalises to lowercase,
 *      and writes `granbay.bc` cookie with a 90-day expiry.
 *   3. The URL param is left as-is — Next.js routing handles it; the
 *      cookie is the durable layer.
 *
 * Read path:
 *   `useBuilderRefCode()` returns the active code (cookie ?? URL
 *   param), or `null`. TradePanel passes it to `placeOrder` as
 *   `builderCodeHex`.
 *
 * Validation is shape-only on the frontend — the server is the
 * authority and rejects unknown / inactive codes with a 400 (see
 * `OrdersService.resolveBuilderCode`). A wrong/expired code in the
 * cookie therefore fails the trade rather than silently dropping
 * attribution; that's deliberate per the STORY-1102 spec ("silent-
 * drop would credit 'direct' attribution when the trader meant to
 * credit a builder").
 */

import { useEffect, useState } from "react";

const COOKIE_NAME = "granbay.bc";
const URL_PARAM = "bc";
const HEX_RE = /^[0-9a-fA-F]{64}$/;
const EXPIRY_DAYS = 90;

export function isValidBuilderRefCode(value: unknown): value is string {
  return typeof value === "string" && HEX_RE.test(value);
}

export function readBuilderRefCookie(): string | null {
  if (typeof document === "undefined") return null;
  const raw = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${COOKIE_NAME}=`));
  if (!raw) return null;
  const value = decodeURIComponent(raw.split("=")[1] || "").toLowerCase();
  return isValidBuilderRefCode(value) ? value : null;
}

export function writeBuilderRefCookie(code: string): void {
  if (typeof document === "undefined") return;
  if (!isValidBuilderRefCode(code)) return;
  const expires = new Date(
    Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${COOKIE_NAME}=${code.toLowerCase()}; path=/; expires=${expires}; samesite=lax`;
}

export function clearBuilderRefCookie(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; samesite=lax`;
}

/**
 * Reads the `bc` URL search param if present. SSR-safe (returns null
 * outside the browser). Used at first-paint to catch the inbound code
 * before it gets washed away by client-side navigation.
 */
function readBuilderRefFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(URL_PARAM);
    if (!raw) return null;
    const lower = raw.toLowerCase();
    return isValidBuilderRefCode(lower) ? lower : null;
  } catch {
    return null;
  }
}

/**
 * Hook variant — captures a fresh `?bc=<hex>` on first mount + reads
 * the persisted cookie thereafter. Returns the active code, or null.
 *
 * Capture takes precedence over an existing cookie: if a user clicks
 * a different builder's link, that builder gets the attribution from
 * that point forward (last-write-wins, 90-day rolling expiry).
 */
export function useBuilderRefCode(): string | null {
  const [code, setCode] = useState<string | null>(null);

  useEffect(() => {
    const fromUrl = readBuilderRefFromUrl();
    if (fromUrl) {
      writeBuilderRefCookie(fromUrl);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCode(fromUrl);
      return;
    }
    setCode(readBuilderRefCookie());
  }, []);

  return code;
}
